<template>
  <AnimatedContainer>
    <Container tag="section" space-y class="px-container sm:px-container-md">
      <!-- TITLE -->
      <h2
        v-if="data.heading"
        class="slide-up mb-6 typo-heading-sm"
        v-html="data.heading"
      />

      <!-- TEXT -->
      <div
        v-if="data.text"
        :style="{ '--wysiwyg-text-color': '#6479AB' }"
        class="wysiwyg-custom-text wysiwyg-a-underline slide-up mb-8 max-w-text md:col-start-2"
        v-html="data.text"
      />

      <!-- CHART -->
      <div class="aspect-video w-full">
        <VChart
          ref="chartElement"
          :style="{
            width: '100%',
            height: '100%',
          }"
          :option="option"
        />
      </div>
    </Container>
  </AnimatedContainer>
</template>

<script setup lang="ts">
import type { MainComponentsChartFragment } from '#gql'

const props = defineProps<{
  data: Enforce<MainComponentsChartFragment>
}>()

const chartElement = ref<null | HTMLDivElement>(null)

const xAxis = computed(() => {
  if (!Array.isArray(props.data.chart_data) || !props.data.chart_data.length) {
    return []
  }
  return props.data.chart_data.slice(1).map((item) => item.cells[0])
})

const series = computed(() => {
  if (
    !Array.isArray(props.data.chart_data) ||
    props.data.chart_data.length <= 1
  ) {
    return []
  }

  const headers = props.data.chart_data[0].cells.slice(1) // Extract headers excluding the first column
  const dataRows = props.data.chart_data.slice(1) // Exclude the header row

  // Create a series for each column, excluding the first column
  return headers.map((header, index) => {
    const colors = ['#0A36A9', '#6479AB']

    const lines = {
      name: header,
      type: 'line',
      smooth: 0.3,
      data: dataRows.map((row) => parseFloat(row.cells[index + 1])),
      itemStyle: {},
      lineStyle: {},
    }

    if (index === 0) {
      lines.lineStyle = {
        width: 3,
      }
    }

    if (index < 2) {
      lines.itemStyle = {
        color: colors[index],
      }
    }

    return lines
  })
})

const option = ref<ECOption>({
  grid: {
    left: '45px', // Remove left padding
    right: '0%', // Remove right padding
    top: '10%', // Adjust top padding if needed
    bottom: '10%', // Adjust bottom padding if needed
  },
  tooltip: {
    trigger: 'axis',
    formatter: (params) => {
      const { axisValue } = params[0] // Get the x-axis category value (e.g., "2000", "2001")
      const seriesData = params
        .map((item) => {
          return `${item.marker}${item.seriesName}: ${item.value}%`
        })
        .join('<br/>')

      return `<strong>${axisValue}</strong><br/>${seriesData}` // Make axis value bold
    },
  },
  xAxis: {
    type: 'category',
    data: xAxis.value,
  },
  yAxis: {
    type: 'value',
    axisLabel: {
      formatter: '{value}%', // Add % postfix to y-axis labels
    },
    splitLine: {
      show: true, // Enable the horizontal lines
      lineStyle: {
        color: '#ccc', // Line color
        width: 1, // Line width (thin)
        type: 'solid', // Line style: 'solid', 'dashed', 'dotted', etc.
      },
    },
  },
  series: series.value,
})

function handleResize() {
  if (chartElement.value) {
    chartElement.value.resize() // Resize the chart instance
  }
}

const debouncedHandleResize = useDebounceFn(() => {
  handleResize()
}, 200)

onMounted(() => {
  window.addEventListener('resize', debouncedHandleResize)
})

onBeforeUnmount(() => {
  // Clean up event listener and chart instance
  window.removeEventListener('resize', debouncedHandleResize)
})
</script>
